import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, ButtonToolbar } from 'reactstrap';
import { formValueSelector, reduxForm } from 'redux-form';
import Immutable from 'immutable';
import debounce from 'lodash/debounce';
import { generateExpenseSapMigrationColumns } from '../../../../config/columns';
import Modal from '../../../common/modal/Modal';
import ExpenseSapMigrationForm from './ExpenseSapMigrationForm';
import { COLOR, DEFAULT_PAGE_SIZE } from '../../../../config/constants';
import { tzNormalizeDate } from '../../../../utils/date';
import { TIMESTAMP_FORMAT } from '../../../../config/locale';
import {
  getChartOfAccounts,
  getExpenseSapMigration,
  getExpenseSapMigrationExcel,
  getExpensesSapMigration,
  postMigrateExpenseSapMigration,
} from '../../../../actions';
import ExpenseSapMigrationDetailView from './ExpenseSapMigrationDetailView';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import { numberFormatter } from '../../../../utils/number';
import EnterFilterSelectionableTable from '../../../common/forms/table/EnterFilterSelectionableTable';
import SaveButton from '../../../common/button/SaveButton';

const MIGRATION_TABLE_PAGE_SIZE = 100;

const ExpenseSapMigrationTableForm = ({
  loading,
  expensesSapMigrationMap,
  dispatchPutExpenseSapMigration,
  selectedExpenses,
  dispatchMigrateExpenses,
  dispatchGetExpensesSapMigration,
  dispatchFetchChartOfAccounts,
  dispatchGetExpenseSapMigrationDetail,
  dispatchExportExcel,
  query,
  searchToken,
  chartOfAccounts,
}) => {
  const [data, setData] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Editar Egreso');
  const [modalBody, setModalBody] = useState(null);
  const [currentTableQuery, setCurrentTableQuery] = useState([]);

  const debouncedFetchRef = useRef(
    debounce((tableState) => {
      dispatchGetExpensesSapMigration(tableState);
    }, 400),
  );

  useEffect(
    () => () => {
      debouncedFetchRef.current.cancel();
    },
    [],
  );

  useEffect(() => {
    if (
      expensesSapMigrationMap &&
      typeof expensesSapMigrationMap.get === 'function'
    ) {
      const content = expensesSapMigrationMap.get('content') || [];
      const newContent = content.map((row) => ({
        ...row,
        modified: false,
      }));
      setData(newContent);
    }
  }, [expensesSapMigrationMap]);

  useEffect(() => {
    dispatchFetchChartOfAccounts();
  }, []);

  const onCloseModal = () => {
    setShowModal(false);
    setModalBody(null);
  };

  const handleMigrateClick = async () => {
    if (!selectedExpenses || selectedExpenses.length === 0) return;

    try {
      await dispatchMigrateExpenses({
        expenseSapMigrationIds: selectedExpenses,
      });
    } catch (error) {
      console.error('Error al migrar:', error);
    }
  };

  const selectedCount = selectedExpenses ? selectedExpenses.length : 0;

  const getDataIndex = ({ id }) => data.findIndex((row) => row.id === id);

  const onSubmitForm = async (formValues) => {
    const dataIndex = getDataIndex({ id: formValues.id });
    if (dataIndex < 0) return;

    const sysCodeValue =
      typeof formValues.sysCode === 'object' && formValues.sysCode !== null
        ? formValues.sysCode.value
        : formValues.sysCode;

    const matchedAccount = chartOfAccounts.find(
      (acc) => acc.Code === sysCodeValue,
    );

    const payload = {
      id: formValues.id,
      agencyId: formValues.agencyId ? formValues.agencyId.value : null,
      costCenterId: formValues.costCenterId
        ? formValues.costCenterId.value
        : null,
      voucherTypeId: formValues.voucherTypeId
        ? formValues.voucherTypeId.value
        : null,
      sysCode: sysCodeValue || null,
      accountCode: matchedAccount ? matchedAccount.FormatCode : null,
      accountName: matchedAccount ? matchedAccount.Name : null,
      expenseTypeName: formValues.expenseTypeName
        ? formValues.expenseTypeName.label
        : null,
      issueDate: formValues.issueDate
        ? tzNormalizeDate({
            date: formValues.issueDate,
            format: TIMESTAMP_FORMAT,
          })
        : tzNormalizeDate({ format: TIMESTAMP_FORMAT }),
      documentSeries: formValues.documentSeries,
      documentCode: formValues.documentCode,
      businessTaxId: formValues.businessTaxId,
      businessName: formValues.businessName,
      description: formValues.description,
      totalAmount: formValues.totalAmount,
    };

    try {
      const updatedEntity = await dispatchPutExpenseSapMigration(
        formValues.id,
        payload,
      );

      const newData = [...data];
      const currentRow = newData[dataIndex];

      newData[dataIndex] = {
        ...currentRow,
        ...updatedEntity,
        modified: false,
      };

      setData(newData);
      onCloseModal();
    } catch (error) {
      console.error('Error al actualizar la fila:', error);
    }
  };

  const pageTotalAmount = data.reduce(
    (sum, row) => sum + (Number(row.totalAmount) || 0),
    0,
  );

  const generateInitialValues = (row) => ({
    id: row.id,
    agencyId: row.agency
      ? { value: row.agency.id, label: row.agency.name }
      : null,
    costCenterId: row.costCenter
      ? {
          value: row.costCenter.id,
          label: row.costCenter.costCenterCode
            ? `${row.costCenter.costCenterCode} - ${row.costCenter.name}`
            : row.costCenter.name,
        }
      : null,
    voucherTypeId: row.voucherType && {
      value: row.voucherType.id,
      label: `${row.voucherType.voucherCode} - ${row.voucherType.name}`,
    },
    sysCode: row.sysCode
      ? {
          value: row.sysCode,
          label:
            row.accountCode && row.accountName
              ? `${row.accountCode} - ${row.accountName}`
              : row.sysCode,
          accountCode: row.accountCode,
          accountName: row.accountName,
        }
      : null,
    issueDate: row.issueDate && tzNormalizeDate({ date: row.issueDate }),
    expenseTypeName: row.expenseTypeName
      ? { value: row.expenseTypeName, label: row.expenseTypeName }
      : null,
    documentSeries: row.documentSeries,
    documentCode: row.documentCode,
    businessTaxId: row.businessTaxId,
    businessName: row.businessName,
    description: row.description,
    totalAmount: row.totalAmount,
  });

  const handleEdit = (row) => {
    const body = (
      <ExpenseSapMigrationForm
        initialValues={generateInitialValues(row)}
        onCancel={onCloseModal}
        onSubmit={onSubmitForm}
      />
    );

    setModalTitle(`Editar Egreso - Cód: ${row.id}`);
    setModalBody(body);
    setShowModal(true);
  };

  const handleView = async (row) => {
    try {
      const detail = await dispatchGetExpenseSapMigrationDetail(row.id);
      setModalTitle(`Detalle Egreso - Cód: ${row.id}`);
      setModalBody(
        <ExpenseSapMigrationDetailView expenseSapMigration={detail} />,
      );
      setShowModal(true);
    } catch (error) {
      console.error('Error al obtener el detalle:', error);
    }
  };

  const onFetchData = useCallback(
    (tableState) => {
      if (!query) return;
      setCurrentTableQuery(tableState.query || []);
      debouncedFetchRef.current(tableState);
    },
    [query],
  );

  const handleExportClick = async () => {
    const tableQueryString = Array.isArray(currentTableQuery)
      ? currentTableQuery.join(',')
      : currentTableQuery;

    const queryString = tableQueryString || query;

    await dispatchExportExcel({ query: queryString });
  };

  const numberOfElements = expensesSapMigrationMap.get('numberOfElements');
  const backendPageSize =
    expensesSapMigrationMap.get('size') || DEFAULT_PAGE_SIZE;
  const currentPageNumber = expensesSapMigrationMap.get('number') || 0;
  const totalPages = expensesSapMigrationMap.get('totalPages') || null;

  const pages =
    numberOfElements != null && numberOfElements < backendPageSize
      ? currentPageNumber + 1
      : totalPages;
  
  const getMigrationRowColor = (row) =>
    row.edited ? COLOR.info : undefined;  

  return (
    <div className="mt-4">
      <ResourceProperty label="Importe Total">
        - {numberFormatter({value: pageTotalAmount})}
      </ResourceProperty>
      <div className="mt-3 mb-2">
        <ButtonToolbar>
          <SaveButton
            action={handleMigrateClick}
            type="button"
            disabled={loading || selectedCount === 0}
            icon="fa fa-cloud-upload"
            text={
              selectedCount > 0
                ? `Migrar a SAP (${selectedCount})`
                : 'Migrar a SAP'
            }
          />{' '}
          <Button
            color="success"
            className="ml-2"
            onClick={handleExportClick}
            disabled={loading || !query}
          >
            <i className="fa fa-file-excel-o mr-1" />
            Exportar a Excel
          </Button>
        </ButtonToolbar>
      </div>
      <EnterFilterSelectionableTable
        columns={generateExpenseSapMigrationColumns({
          handleEdit,
          handleView,
        })}
        data={data}
        defaultPageSize={MIGRATION_TABLE_PAGE_SIZE}
        keyField="id"
        loading={loading}
        pages={pages}
        form="ExpenseSapMigrationTableForm"
        name="selectedExpenses"
        isItemSelectable={(row) =>
          !row.migrationStatus ||
          row.migrationStatus === 'PENDING' ||
          row.migrationStatus === 'ERROR'
        }
        filterable
        returnOnlySelectedItems
        queryProps={query}
        manual
        fetchData={onFetchData}
        showPageJump={false}
        searchOnEnter
        refreshKey={searchToken}
        blockEnterSubmit
        rowColorResolver={getMigrationRowColor}
      />

      <Modal
        show={showModal}
        title={modalTitle}
        onClickClose={onCloseModal}
        body={modalBody}
        size="lg"
      />
    </div>
  );
};

ExpenseSapMigrationTableForm.propTypes = {
  loading: PropTypes.bool,
  expensesSapMigrationMap: PropTypes.instanceOf(Immutable.Map).isRequired,
  dispatchMigrateExpenses: PropTypes.func.isRequired,
  dispatchPutExpenseSapMigration: PropTypes.func.isRequired,
  dispatchGetExpensesSapMigration: PropTypes.func.isRequired,
  dispatchGetExpenseSapMigrationDetail: PropTypes.func.isRequired,
  dispatchFetchChartOfAccounts: PropTypes.func.isRequired,
  dispatchExportExcel: PropTypes.func.isRequired,
  selectedExpenses: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  query: PropTypes.string,
  chartOfAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      Code: PropTypes.string,
      FormatCode: PropTypes.string,
      Name: PropTypes.string,
      FrozenFor: PropTypes.string,
    }),
  ),
  searchToken: PropTypes.number,
};

ExpenseSapMigrationTableForm.defaultProps = {
  loading: false,
  selectedExpenses: null,
  query: null,
  chartOfAccounts: [],
  searchToken: 0,
};

const mapDispatchToProps = {
  dispatchMigrateExpenses: postMigrateExpenseSapMigration,
  dispatchGetExpensesSapMigration: getExpensesSapMigration,
  dispatchFetchChartOfAccounts: getChartOfAccounts,
  dispatchGetExpenseSapMigrationDetail: getExpenseSapMigration,
  dispatchExportExcel: getExpenseSapMigrationExcel,
};

const selector = formValueSelector('ExpenseSapMigrationTableForm');

const mapStateToProps = (state) => {
  const { AccountingUnit } = state;
  const chartOfAccountContent = AccountingUnit.ChartOfAccount.getIn([
    'all',
    'content',
  ]);
  return {
    loading: AccountingUnit.ExpenseSapMigration.getIn(['all', 'loading']),
    expensesSapMigrationMap: AccountingUnit.ExpenseSapMigration.getIn([
      'all',
      'content',
    ]),
    selectedExpenses: selector(state, 'selectedExpenses'),
    chartOfAccounts: chartOfAccountContent ? chartOfAccountContent.toJS() : [],
  };
};

const formWrapped = reduxForm({
  form: 'ExpenseSapMigrationTableForm',
})(ExpenseSapMigrationTableForm);

export default connect(mapStateToProps, mapDispatchToProps)(formWrapped);
